import React from 'react';

import { FmBanner } from './FmBanner';
import { FmButton } from './FmButton';
import { FmSplit } from './FmLayout';
import { FmInline, FmRoutePage, FmStack } from './FmComposition';
import { AnalysisWorkspaceView } from './AnalysisWorkspaceViews';
import { FmDatedRasterCompareProvider } from './FmDatedRasterCompareProvider';
import { FmMapViewportControls } from './FmMapViewportControls';
import { FmMapViewportProvider } from './FmMapViewportState';
import { FmSelector } from './FmSelector';
import type {
  FmAnalysisWorkspaceDescriptor,
  FmAnalysisWorkspaceRootProps,
} from './FmAnalysisWorkspaceRoot.types';
import { validateAnalysisWorkspaceDescriptor } from './FmAnalysisWorkspaceRoot.validation';
import { AnalysisWorkspaceCompareOverlay } from './internal/AnalysisWorkspaceCompareOverlay';
import { AnalysisWorkspaceDialogs } from './internal/AnalysisWorkspaceDialogs';
import {
  AnalysisWorkspaceActionButtons,
  AnalysisWorkspaceEvidenceFilters,
  AnalysisWorkspaceFilters,
  AnalysisWorkspaceHeader,
  AnalysisWorkspaceSeriesRefineSheet,
} from './internal/AnalysisWorkspaceHeader';
import { analysisWorkspaceChromeActions } from './internal/analysisWorkspaceChrome';
import {
  AnalysisWorkspaceDateChip,
  AnalysisWorkspaceTransport,
} from './internal/AnalysisWorkspaceTransport';
import { FmMapCanvas, FmMapStage } from '@/shared/ui/FmMapStage';
import { getMapSlot, WORKSPACE_PRIMARY_MAP_SLOT_KEY } from '@/shared/config/mapSlotRegistry';
import './FmAnalysisWorkspaceRoot.css';
import './FmAnalysisWorkspaceRoot.stacked.css';
import './FmAnalysisWorkspaceRoot.series.css';
import { fmMapOcclusion } from '@/shared/ui/mapOcclusion';

function AnalysisWorkspacePaneSwitch({
  descriptor,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
}) {
  return (
    <div className="fm-analysis-workspace__pane-switch">
      <FmSelector
        variant="segmented"
        label={descriptor.copy.viewsLabel}
        value={descriptor.activePane}
        onChange={(value) => descriptor.onPaneChange(value as 'map' | 'analysis')}
        items={[
          { value: 'map', label: descriptor.copy.mapPane },
          { value: 'analysis', label: descriptor.copy.analysisPane },
        ]}
      />
    </div>
  );
}

function datedRasterComparison(
  map: FmAnalysisWorkspaceDescriptor['map'],
): { earlierLabel: string; laterLabel: string } | null {
  if (map.datedRaster?.status !== 'ready' || !map.datedRaster.compared || !map.comparison) {
    return null;
  }
  if (!map.comparison.earlierLabel.trim() || !map.comparison.laterLabel.trim()) return null;
  return map.comparison;
}

function mapSelectedFeatureKind(
  map: FmAnalysisWorkspaceDescriptor['map'],
): 'plot' | 'stand' | undefined {
  if (map.selectedPlotId) return 'plot';
  if (map.selectedStandId) return 'stand';
  return undefined;
}

function AnalysisWorkspaceIndexLegend({
  descriptor,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
}) {
  const view =
    descriptor.views.find(({ id }) => id === descriptor.activeViewId) ?? descriptor.views[0];
  if (view?.kind !== 'satellite-index' || !view.legend) return null;
  return (
    <div
      className="fm-analysis-workspace__index-legend"
      data-testid="analysis-workspace-index-legend"
    >
      <span className="fm-analysis-workspace__index-legend-label">{view.legend.lowLabel}</span>
      <span
        className="fm-analysis-workspace__index-ramp"
        data-index={view.legend.index}
        aria-hidden
      />
      <span className="fm-analysis-workspace__index-legend-label">{view.legend.highLabel}</span>
    </div>
  );
}

function WorkspaceMapChrome({
  descriptor,
  comparison,
  presentation,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
  comparison: { earlierLabel: string; laterLabel: string } | null;
  presentation: 'workspace' | 'map-detail';
}) {
  const transport = descriptor.map.timeSeries;
  const activeView =
    descriptor.views.find(({ id }) => id === descriptor.activeViewId) ?? descriptor.views[0];
  const hasLegend = activeView?.kind === 'satellite-index' && Boolean(activeView.legend);
  return (
    <>
      <div className="fm-analysis-workspace__map-top">
        {transport || hasLegend ? (
          <div className="fm-analysis-workspace__map-meta" {...fmMapOcclusion('top', 'panel')}>
            {transport ? (
              <AnalysisWorkspaceDateChip
                title={descriptor.title}
                label={comparison ? '' : transport.currentDateLabel}
              />
            ) : null}
            <AnalysisWorkspaceIndexLegend descriptor={descriptor} />
          </div>
        ) : null}
        {transport ? (
          <div className="fm-analysis-workspace__map-fit" {...fmMapOcclusion('top', 'panel')}>
            {presentation === 'workspace' ? (
              <AnalysisWorkspacePaneSwitch descriptor={descriptor} />
            ) : null}
            <FmInline gap="sm" wrap>
              <FmInline gap="sm" wrap className="fm-analysis-workspace__plate-controls">
                <AnalysisWorkspaceFilters descriptor={descriptor} />
                <AnalysisWorkspaceActionButtons
                  actions={analysisWorkspaceChromeActions(descriptor)}
                  onAction={descriptor.onAction}
                  secondaryVariant="default"
                />
              </FmInline>
              <AnalysisWorkspaceSeriesRefineSheet descriptor={descriptor} />
              <FmMapViewportControls
                fitLabel={descriptor.copy.fitMap}
                fitOnly
                variant="default"
              />
            </FmInline>
          </div>
        ) : null}
      </div>
      {transport ? null : (
        // Without a series the fit command is the only map tool beside zoom: it
        // sits in the free bottom-start corner (zoom owns top-start, attribution
        // bottom-end) instead of a plate over the subject.
        <div className="fm-analysis-workspace__map-corner" {...fmMapOcclusion('bottom', 'panel')}>
          <FmMapViewportControls fitLabel={descriptor.copy.fitMap} fitOnly variant="default" />
        </div>
      )}
      {comparison ? (
        <AnalysisWorkspaceCompareOverlay
          earlierLabel={comparison.earlierLabel}
          laterLabel={comparison.laterLabel}
        />
      ) : null}
      {transport ? (
        <AnalysisWorkspaceTransport descriptor={descriptor} transport={transport} />
      ) : null}
    </>
  );
}

function WorkspaceMapStage({
  descriptor,
  preview,
  comparison,
  presentation,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
  preview: boolean;
  comparison: { earlierLabel: string; laterLabel: string } | null;
  presentation: 'workspace' | 'map-detail';
}) {
  const MapSlot = getMapSlot(WORKSPACE_PRIMARY_MAP_SLOT_KEY);
  const mapData = descriptor.map.mapData;
  if (!MapSlot) return null;
  return (
    <FmMapStage fill className="fm-analysis-workspace__map-stage">
      <FmMapViewportProvider>
        <FmMapCanvas dark>
          <MapSlot
            propertyId={descriptor.propertyId}
            selectedFeatureId={
              descriptor.map.selectedPlotId ?? descriptor.map.selectedStandId ?? undefined
            }
            selectedFeatureKind={mapSelectedFeatureKind(descriptor.map)}
            selectedPlotId={descriptor.map.selectedPlotId ?? undefined}
            selectedStandId={descriptor.map.selectedStandId ?? undefined}
            measurementPlotRefs={descriptor.map.plotRefs}
            layerIds={
              descriptor.map.layerIds ?? ['property-boundary', 'forest-stands', 'measurement-plots']
            }
            baseMapId={descriptor.map.baseMapId ?? 'streets'}
            datedRaster={descriptor.map.datedRaster}
            interactionMode="explore"
            workspaceMode="inspect"
            onStandSelect={(standId) => descriptor.onStandSelect(standId)}
            onPlotSelect={(plotId) => descriptor.onPlotSelect(plotId)}
            {...(preview
              ? { preview: true as const, mapData: null }
              : { mapData: mapData as NonNullable<typeof mapData> })}
          />
        </FmMapCanvas>
        <WorkspaceMapChrome
          descriptor={descriptor}
          comparison={comparison}
          presentation={presentation}
        />
      </FmMapViewportProvider>
    </FmMapStage>
  );
}

function WorkspaceMap({
  descriptor,
  preview,
  presentation,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
  preview: boolean;
  presentation: 'workspace' | 'map-detail';
}) {
  const MapSlot = getMapSlot(WORKSPACE_PRIMARY_MAP_SLOT_KEY);
  const comparison = datedRasterComparison(descriptor.map);
  if (!MapSlot || (!preview && !descriptor.map.mapData)) {
    return (
      <output className="fm-analysis-workspace__map-unavailable">
        {descriptor.copy.mapUnavailable}
      </output>
    );
  }
  const stage = (
    <WorkspaceMapStage
      descriptor={descriptor}
      preview={preview}
      comparison={comparison}
      presentation={presentation}
    />
  );
  if (!comparison) return stage;
  return (
    <FmDatedRasterCompareProvider key={`${comparison.earlierLabel}:${comparison.laterLabel}`}>
      {stage}
    </FmDatedRasterCompareProvider>
  );
}

function WorkspaceAnalysisPane({
  descriptor,
  presentation,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
  presentation: 'workspace' | 'map-detail';
}) {
  const activeView =
    descriptor.views.find(({ id }) => id === descriptor.activeViewId) ?? descriptor.views[0];
  return (
    <aside
      className={[
        'fm-analysis-workspace__analysis',
        presentation === 'workspace' &&
          descriptor.activePane !== 'analysis' &&
          'fm-analysis-workspace__pane--inactive-mobile',
      ]
        .filter(Boolean)
        .join(' ')}
      {...(descriptor.map.timeSeries ? fmMapOcclusion('panel', 'panel') : {})}
    >
      <FmStack gap="md">
        {descriptor.map.timeSeries && presentation === 'workspace' ? (
          <AnalysisWorkspacePaneSwitch descriptor={descriptor} />
        ) : null}
        <AnalysisWorkspaceEvidenceFilters descriptor={descriptor} />
        {descriptor.state === 'partial' && descriptor.stateReasons.length > 0 ? (
          <FmBanner tone="info" description={descriptor.stateReasons.join(' · ')} />
        ) : null}
        {descriptor.state === 'stale' && descriptor.stateReasons.length > 0 ? (
          <FmBanner tone="warning" description={descriptor.stateReasons.join(' · ')} />
        ) : null}
        <FmSelector
          variant="segmented"
          className="fm-analysis-workspace__views"
          label={descriptor.copy.viewsLabel}
          value={activeView.id}
          onChange={descriptor.onViewChange}
          items={descriptor.views.map((view) => ({
            value: view.id,
            label: view.label,
          }))}
        />
        <AnalysisWorkspaceView view={activeView} copy={descriptor.copy} />
      </FmStack>
    </aside>
  );
}

function WorkspaceReadyBody({
  descriptor,
  presentation,
  preview,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
  presentation: 'workspace' | 'map-detail';
  preview: boolean;
}) {
  const analysis = (
    <WorkspaceAnalysisPane descriptor={descriptor} presentation={presentation} />
  );
  if (presentation === 'map-detail') return analysis;
  const map = (
    <div
      className={[
        'fm-analysis-workspace__map',
        descriptor.activePane !== 'map' && 'fm-analysis-workspace__pane--inactive-mobile',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <WorkspaceMap descriptor={descriptor} preview={preview} presentation={presentation} />
    </div>
  );
  // A time series floats its analysis pane over the full-bleed map.
  if (descriptor.map.timeSeries) {
    return (
      <>
        {map}
        {analysis}
      </>
    );
  }
  // The evidence is what the reader reads (a 30-day series, a distribution), so
  // the analysis pane takes the dominant media track and the map orients beside it.
  return (
    <FmSplit
      side="end"
      balance="media-dominant"
      align="stretch"
      contentSurface="plain"
      className="fm-analysis-workspace__split"
      graphic={analysis}
      content={map}
    />
  );
}

function WorkspaceBody({
  descriptor,
  presentation,
  preview,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
  presentation: 'workspace' | 'map-detail';
  preview: boolean;
}) {
  if (descriptor.state === 'loading') {
    return <output>{descriptor.copy.loading}</output>;
  }
  if (descriptor.state === 'error') {
    return (
      <FmBanner
        tone="error"
        title={descriptor.copy.error}
        action={
          <FmButton variant="bordered" onClick={descriptor.onRetry}>
            {descriptor.copy.retry}
          </FmButton>
        }
      />
    );
  }
  if (descriptor.state === 'empty') {
    return <FmBanner tone="info" title={descriptor.copy.empty} />;
  }
  if (descriptor.state === 'locked') {
    return (
      <FmBanner
        tone="info"
        title={descriptor.copy.locked}
        action={
          <FmButton variant="primary" onClick={descriptor.onLockedAction}>
            {descriptor.copy.lockedAction}
          </FmButton>
        }
      />
    );
  }
  return (
    <WorkspaceReadyBody
      descriptor={descriptor}
      presentation={presentation}
      preview={preview}
    />
  );
}

/** @internal Already-validated surface for sealed archetype composition. */
export const AnalysisWorkspaceSurface: React.FC<{
  descriptor: FmAnalysisWorkspaceDescriptor;
  presentation?: 'workspace' | 'map-detail';
  preview?: boolean;
  'data-testid'?: string;
}> = ({ descriptor, presentation = 'workspace', preview = false, 'data-testid': testId }) => (
  <div
    className={[
      'fm-analysis-workspace',
      presentation === 'map-detail' && 'fm-analysis-workspace--map-detail',
      descriptor.map.timeSeries && 'fm-analysis-workspace--series',
    ]
      .filter(Boolean)
      .join(' ')}
    data-fm-map-workspace
    data-fm-map-fit={descriptor.map.fitCoverage ?? 'contain'}
    data-dated-raster={descriptor.map.datedRaster?.status ?? ''}
    data-testid={testId ?? 'fm-analysis-workspace'}
    data-presentation={presentation}
  >
    {descriptor.map.timeSeries ? null : (
      <AnalysisWorkspaceHeader descriptor={descriptor} />
    )}
    {presentation === 'workspace' && !descriptor.map.timeSeries ? (
      <AnalysisWorkspacePaneSwitch descriptor={descriptor} />
    ) : null}
    <main className="fm-analysis-workspace__body">
      <WorkspaceBody descriptor={descriptor} presentation={presentation} preview={preview} />
    </main>
    <AnalysisWorkspaceDialogs descriptor={descriptor} />
  </div>
);
AnalysisWorkspaceSurface.displayName = 'AnalysisWorkspaceSurface';

export const FmAnalysisWorkspaceRoot: React.FC<FmAnalysisWorkspaceRootProps> = ({
  descriptor,
  preview = false,
  'data-testid': testId,
}) => {
  validateAnalysisWorkspaceDescriptor(descriptor);
  const body = (
    <AnalysisWorkspaceSurface descriptor={descriptor} preview={preview} data-testid={testId} />
  );
  if (preview) return body;
  return (
    <FmRoutePage width="flush" surface="transparent" fit="shell" grow="fill" scroll="hidden">
      {body}
    </FmRoutePage>
  );
}

FmAnalysisWorkspaceRoot.displayName = 'FmAnalysisWorkspaceRoot';
